import {
  applyHandoutExportKeepTogether,
  computeHandoutPageSliceStarts,
  resolveLiveHandoutPaginationPlan
} from './handoutPagination'
import { resolveCosImageUrl } from './renderHtml'

const PX_TO_PT = 0.75
const PDF_EXPORT_CLASS = 'handout-pdf-export'

const PDF_EXPORT_STRIP_STYLE = `
.handout-pdf-export .w-e-text-container,
.handout-pdf-export .handout-body-surface,
.handout-pdf-export [data-slate-editor] {
  background-image: none !important;
  background-color: #fff !important;
  box-shadow: none !important;
}
.handout-pdf-export .handout-page-break-token,
.handout-pdf-export [data-handout-page-break="true"],
.handout-pdf-export .handout-page-break {
  background: transparent !important;
  background-image: none !important;
  box-shadow: none !important;
}
.handout-pdf-export .handout-page-break-token::before,
.handout-pdf-export .handout-page-break-token::after,
.handout-pdf-export [data-handout-page-break="true"]::before,
.handout-pdf-export [data-handout-page-break="true"]::after {
  display: none !important;
  content: none !important;
  box-shadow: none !important;
  background: transparent !important;
  border: none !important;
}
.handout-pdf-export .handout-page-break-label {
  display: none !important;
}
.handout-pdf-export .handout-page-numbers-overlay,
.handout-pdf-export .handout-page-number,
.handout-pdf-export .handout-preview-page-number-layer,
.handout-pdf-export .handout-preview-page-number {
  display: none !important;
}
`

function saveInlineStyles(el, props) {
  const prev = {}
  props.forEach((key) => { prev[key] = el.style[key] })
  return prev
}

function restoreInlineStyles(saved) {
  saved.forEach(({ el, prev }) => {
    Object.keys(prev).forEach((key) => {
      el.style[key] = prev[key]
    })
  })
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/** 开发环境经同源代理拉 COS 图，避免 html2canvas 跨域污染 */
function toExportFetchUrl(src) {
  const resolved = resolveCosImageUrl(src)
  if (typeof window === 'undefined') return resolved
  try {
    const parsed = new URL(resolved, window.location.origin)
    const host = parsed.hostname.toLowerCase()
    if (host.includes('myqcloud.com') || host.includes('shuatibao.com.cn')) {
      return `/handout-image-proxy${parsed.pathname}${parsed.search}`
    }
  } catch (e) { /* ignore */ }
  return resolved
}

async function fetchImageDataUrl(src) {
  const url = toExportFetchUrl(src)
  const response = await fetch(url, { credentials: 'omit', cache: 'force-cache' })
  if (!response.ok) throw new Error(`image fetch ${response.status}`)
  return blobToDataUrl(await response.blob())
}

/** 导出前把外链图转为 data URL，否则 html2canvas 无法绘制且 canvas 会被污染 */
async function inlineExportImages(root) {
  if (!root) return () => {}
  const images = Array.from(root.querySelectorAll('img'))
  const saved = []
  await Promise.all(images.map(async (img) => {
    let rawSrc = (img.getAttribute('src') || img.getAttribute('data-href') || '').trim()
    if (!rawSrc || rawSrc.startsWith('data:')) return
    rawSrc = resolveCosImageUrl(rawSrc)
    saved.push({
      img,
      prevSrc: img.getAttribute('src') || '',
      prevHref: img.getAttribute('data-href') || '',
      prevCrossOrigin: img.crossOrigin
    })
    try {
      const dataUrl = await fetchImageDataUrl(rawSrc)
      img.crossOrigin = 'anonymous'
      img.src = dataUrl
      img.setAttribute('data-href', dataUrl)
      await new Promise((resolve, reject) => {
        if (img.complete && img.naturalWidth > 0) {
          resolve()
          return
        }
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('image reload failed'))
      })
    } catch (e) {
      console.warn('[handout-pdf] inline image failed:', rawSrc, e)
    }
  }))
  return () => {
    saved.forEach(({ img, prevSrc, prevHref, prevCrossOrigin }) => {
      if (prevSrc) img.setAttribute('src', prevSrc)
      else img.removeAttribute('src')
      if (prevHref) img.setAttribute('data-href', prevHref)
      else img.removeAttribute('data-href')
      img.crossOrigin = prevCrossOrigin || ''
    })
  }
}

function findTitleBanner(captureEl, richEditor) {
  if (richEditor) {
    const fromEditor = richEditor.querySelector('.handout-title-banner')
    if (fromEditor) return fromEditor
  }
  return captureEl ? captureEl.querySelector('.handout-title-banner') : null
}

/** 固定导出宽度；隐藏 banner（标题由第一页 Canvas 重绘，避免 html2canvas 宽画布错位） */
function preparePdfCaptureLayout(captureEl, richEditor, contentWidthPx) {
  if (!captureEl) return () => {}
  const widthPx = Math.max(320, Math.round(Number(contentWidthPx) || 760))
  const saved = []
  const chain = [captureEl]
  if (richEditor) chain.push(richEditor)
  const previewPages = captureEl.closest('.preview-pages')
  if (previewPages) chain.push(previewPages)

  chain.forEach((el) => {
    saved.push({ el, prev: saveInlineStyles(el, ['width', 'maxWidth', 'position', 'overflow', 'margin']) })
    el.style.width = `${widthPx}px`
    el.style.maxWidth = `${widthPx}px`
    el.style.overflow = 'hidden'
    if (el === previewPages) el.style.margin = '0'
  })
  if (!captureEl.style.position) captureEl.style.position = 'relative'

  const banner = findTitleBanner(captureEl, richEditor)
  if (banner) {
    saved.push({ el: banner, prev: saveInlineStyles(banner, ['display', 'visibility']) })
    banner.style.display = 'none'
    banner.style.visibility = 'hidden'
  }

  return () => {
    restoreInlineStyles(saved)
  }
}

/** html2canvas 常输出比预期更宽的画布，裁掉右侧空白，避免标题/正文偏右 */
function normalizeCaptureCanvas(rawCanvas, expectedWidth) {
  const targetWidth = Math.max(1, Math.round(expectedWidth))
  if (rawCanvas.width <= targetWidth) {
    return { canvas: rawCanvas, drawWidth: rawCanvas.width }
  }
  const trimmed = document.createElement('canvas')
  trimmed.width = targetWidth
  trimmed.height = rawCanvas.height
  const ctx = trimmed.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, targetWidth, trimmed.height)
  ctx.drawImage(rawCanvas, 0, 0, targetWidth, rawCanvas.height, 0, 0, targetWidth, rawCanvas.height)
  return { canvas: trimmed, drawWidth: targetWidth }
}

/** 第一页顶部重绘居中标题（跳过 html2canvas 截到的错位标题区） */
function paintPdfTitleBand(ctx, widthPx, titleOffsetPx, titleText, pixelScale) {
  const bandHeight = Math.max(0, Math.round(titleOffsetPx * pixelScale))
  if (bandHeight <= 0 || !titleText || widthPx <= 0) return

  ctx.save()
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, widthPx, bandHeight)

  const fontSize = Math.max(12, Math.round(28 * pixelScale))
  ctx.fillStyle = '#1677ff'
  ctx.font = `700 ${fontSize}px "Microsoft YaHei", "微软雅黑", sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(titleText, Math.round(widthPx / 2), Math.round(bandHeight * 0.46))
  ctx.restore()
}

function drawPageSlice(ctx, sourceCanvas, opts) {
  const {
    startPx, drawCanvasWidth, sliceH,
    pageIndex, titleBandPx, titleOffsetPx, displayTitle, heightScale
  } = opts

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, drawCanvasWidth, sliceH)

  if (pageIndex === 0 && titleBandPx > 0) {
    const bodySliceH = Math.max(0, sliceH - titleBandPx)
    const sourceY = startPx + titleBandPx
    if (bodySliceH > 0) {
      ctx.drawImage(
        sourceCanvas,
        0, sourceY, drawCanvasWidth, bodySliceH,
        0, titleBandPx, drawCanvasWidth, bodySliceH
      )
    }
    paintPdfTitleBand(ctx, drawCanvasWidth, titleOffsetPx, displayTitle, heightScale)
    return
  }

  ctx.drawImage(
    sourceCanvas,
    0, startPx, drawCanvasWidth, sliceH,
    0, 0, drawCanvasWidth, sliceH
  )
}

function stripPageBreakPaintForExport(root) {
  if (!root) return () => {}
  const nodes = root.querySelectorAll(
    '.handout-page-break-token,[data-handout-page-break="true"],.handout-page-break'
  )
  const saved = []
  nodes.forEach((node) => {
    saved.push({ node, cssText: node.style.cssText })
    node.style.setProperty('background', 'transparent', 'important')
    node.style.setProperty('background-image', 'none', 'important')
    node.style.setProperty('box-shadow', 'none', 'important')
  })
  return () => {
    saved.forEach(({ node, cssText }) => {
      node.style.cssText = cssText
    })
  }
}

function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

function waitForImages(root) {
  if (!root) return Promise.resolve()
  const images = Array.from(root.querySelectorAll('img'))
  const pending = images.filter((img) => !img.complete)
  if (!pending.length) return Promise.resolve()
  return Promise.all(pending.map((img) => new Promise((resolve) => {
    const done = () => resolve()
    img.addEventListener('load', done, { once: true })
    img.addEventListener('error', done, { once: true })
  })))
}

function resolveCaptureElement(exportRoot) {
  if (!exportRoot) return null
  const richEditor = exportRoot.closest('.rich-editor')
  const editorScroll = richEditor
    ? richEditor.querySelector('.editor-scroll')
    : (exportRoot.matches && exportRoot.matches('.editor-scroll') ? exportRoot : null)
  if (editorScroll) return editorScroll
  if (exportRoot.matches && exportRoot.matches('[data-slate-editor]')) return exportRoot
  const fromRoot = exportRoot.querySelector
    ? (exportRoot.querySelector('.editor-scroll') || exportRoot.querySelector('[data-slate-editor]'))
    : null
  if (fromRoot) return fromRoot
  return exportRoot.closest('.preview-pages') || exportRoot
}

function resetScrollForCapture(captureEl) {
  const scrollers = []
  let node = captureEl
  while (node) {
    if (node.scrollTop > 0) {
      scrollers.push({ node, top: node.scrollTop })
      node.scrollTop = 0
    }
    node = node.parentElement
  }
  const winY = window.scrollY || 0
  if (winY > 0) window.scrollTo(0, 0)
  return () => {
    scrollers.forEach(({ node, top }) => { node.scrollTop = top })
    if (winY > 0) window.scrollTo(0, winY)
  }
}

/**
 * 从与讲义正文相同的 RichEditor 预览 DOM 按页高切片导出 PDF，分页与编辑器一致
 */
export async function exportHandoutPreviewToPdf(exportRoot, options = {}) {
  const captureEl = resolveCaptureElement(exportRoot)
  if (!captureEl) {
    throw new Error('缺少导出区域')
  }
  const { title = '讲义', contentHtml = '', versionLabel = '学生版' } = options

  const restoreScroll = resetScrollForCapture(captureEl)
  let restoreKeepTogether = () => {}
  let restorePageBreakPaint = () => {}
  let restoreCaptureLayout = () => {}
  let restoreInlineImages = () => {}
  try {
    await waitForPaint()
    await waitForImages(captureEl)

    const richEditor = captureEl.closest('.rich-editor')
    const styleTargets = [captureEl]
    if (richEditor) styleTargets.push(richEditor)
    styleTargets.forEach((el) => el.classList.add(PDF_EXPORT_CLASS))

    const surface = captureEl.matches('[data-slate-editor]')
      ? captureEl
      : captureEl.querySelector('[data-slate-editor]')
    let plan = resolveLiveHandoutPaginationPlan(captureEl, contentHtml)
    const contentWidthPx = plan.contentWidth > 0
      ? plan.contentWidth
      : Math.round((surface && surface.clientWidth) || captureEl.clientWidth || 760)

    const banner = findTitleBanner(captureEl, richEditor)
    const displayTitle = (title || (banner && banner.textContent) || '未命名讲义').trim()
    let titleOffsetPx = Math.max(0, Number(plan.titleOffsetPx) || 0)
    if (!titleOffsetPx && banner) {
      titleOffsetPx = Math.round(banner.offsetHeight || 0)
    }
    if (!titleOffsetPx && surface) {
      const padTop = parseFloat(window.getComputedStyle(surface).paddingTop) || 0
      titleOffsetPx = Math.round(padTop)
    }
    if (!titleOffsetPx) {
      titleOffsetPx = 68
    }

    restoreCaptureLayout = preparePdfCaptureLayout(captureEl, richEditor, contentWidthPx)
    if (typeof options.syncTitleBanner === 'function') {
      options.syncTitleBanner()
    }
    restoreInlineImages = await inlineExportImages(surface || captureEl)
    restorePageBreakPaint = stripPageBreakPaintForExport(surface || captureEl)
    await waitForPaint()
    await waitForImages(surface || captureEl)

    restoreKeepTogether = surface
      ? applyHandoutExportKeepTogether(surface, plan)
      : () => {}
    await waitForPaint()

    plan = resolveLiveHandoutPaginationPlan(captureEl, contentHtml)
    const captureTarget = surface || captureEl
    const sliceInfo = computeHandoutPageSliceStarts(captureEl, plan, { sliceRoot: captureTarget })
    if (!sliceInfo || !sliceInfo.starts.length) {
      throw new Error('无法计算分页')
    }

    const captureWidth = contentWidthPx
    const captureHeight = sliceInfo.totalHeight || Math.max(
      captureTarget.scrollHeight || 0,
      captureTarget.offsetHeight || 0
    )

    const scale = 2
    const html2canvas = (await import('html2canvas')).default
    const { jsPDF } = await import('jspdf')

    const rawCanvas = await html2canvas(captureTarget, {
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      x: 0,
      y: 0,
      width: captureWidth,
      height: captureHeight,
      windowWidth: captureWidth,
      scrollX: 0,
      scrollY: 0,
      onclone: (doc) => {
        doc.querySelectorAll(`.${PDF_EXPORT_CLASS}`).forEach((el) => {
          el.classList.add(PDF_EXPORT_CLASS)
        })
        doc.querySelectorAll('.handout-title-banner').forEach((el) => {
          el.style.display = 'none'
          el.style.visibility = 'hidden'
        })
        const style = doc.createElement('style')
        style.setAttribute('data-handout-pdf-export', 'true')
        style.textContent = PDF_EXPORT_STRIP_STYLE
        doc.head.appendChild(style)
      }
    })

    styleTargets.forEach((el) => el.classList.remove(PDF_EXPORT_CLASS))

    const heightScale = captureHeight > 0 ? (rawCanvas.height / captureHeight) : scale
    const expectedCanvasWidth = Math.max(1, Math.round(contentWidthPx * heightScale))
    const { canvas, drawWidth: drawCanvasWidth } = normalizeCaptureCanvas(rawCanvas, expectedCanvasWidth)
    const titleBandPx = Math.round(titleOffsetPx * heightScale)
    const pageWidthPt = Math.max(1, contentWidthPx * PX_TO_PT)
    const pageCount = sliceInfo.pageCount
    const plannedHeightPx = Math.round(sliceInfo.totalHeight * heightScale)
    let pdf = null

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const startPx = Math.round(sliceInfo.starts[pageIndex] * heightScale)
      const endPx = pageIndex + 1 < pageCount
        ? Math.round(sliceInfo.starts[pageIndex + 1] * heightScale)
        : plannedHeightPx
      if (startPx >= plannedHeightPx) break

      const sliceH = Math.max(1, Math.min(endPx - startPx, plannedHeightPx - startPx))
      const pageHeightPt = (sliceH / heightScale) * PX_TO_PT

      if (!pdf) {
        pdf = new jsPDF({
          unit: 'pt',
          format: [pageWidthPt, pageHeightPt],
          compress: true
        })
      } else {
        pdf.addPage([pageWidthPt, pageHeightPt])
      }

      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = drawCanvasWidth
      pageCanvas.height = sliceH
      const ctx = pageCanvas.getContext('2d')
      drawPageSlice(ctx, canvas, {
        startPx,
        drawCanvasWidth,
        sliceH,
        pageIndex,
        titleBandPx,
        titleOffsetPx,
        displayTitle,
        heightScale
      })
      const imgData = pageCanvas.toDataURL('image/jpeg', 0.95)
      pdf.addImage(imgData, 'JPEG', 0, 0, pageWidthPt, pageHeightPt)
    }

    if (!pdf) {
      throw new Error('导出内容为空')
    }

    pdf.save(`${displayTitle}_${versionLabel || '学生版'}.pdf`)
  } finally {
    restoreKeepTogether()
    restorePageBreakPaint()
    restoreInlineImages()
    restoreCaptureLayout()
    restoreScroll()
    const richEditor = captureEl.closest('.rich-editor')
    ;[captureEl, richEditor].filter(Boolean).forEach((el) => el.classList.remove(PDF_EXPORT_CLASS))
  }
}
